import { Component, inject, OnInit, signal } from '@angular/core';
import { HeaderService } from '../../services/header.service';
import { CommonModule } from '@angular/common';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ThemeService } from '../../services/theme.service';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ChangePasswordComponent } from '../../components/change-password/change-password.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { UsersService } from '../../services/user.service';
import { User } from '../../interfaces/user';
import { InputComponent } from '../../components/input/input.component';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    InputSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    ChangePasswordComponent,
    CardModule,
    DividerModule,
    TooltipModule,
    InputComponent,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './settings.component.html',
})
export class SettingsComponent implements OnInit {
  private readonly _headerService = inject(HeaderService);
  private readonly _router = inject(Router);
  private readonly _messageService = inject(MessageService);
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UsersService);
  private readonly _fb = inject(FormBuilder);
  private readonly _confirmationService = inject(ConfirmationService);

  readonly themeService = inject(ThemeService);

  protected visibleSecurity = false;
  protected visibleProfile = false;
  protected darkMode = false;
  protected isSavingProfile = false;
  protected user = signal<User | null>(null);
  protected formProfile!: FormGroup;

  ngOnInit(): void {
    this._headerService.setHeader('Ajustes', false, 'pi pi-cog', 'bg-gray-100', 'text-gray-500');
    this.darkMode = this.themeService.isDarkMode;
    this.initProfileForm();
    this.fetchUserProfile();
  }

  private initProfileForm() {
    this.formProfile = this._fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
    });
  }

  private fetchUserProfile() {
    this._authService.fetchUserRole().subscribe({
      next: (user) => {
        this.user.set(user);
        this.formProfile.patchValue(user);
      },
      error: (err) => console.error('Error fetching user profile:', err)
    });
  }

  showProfileDialog() {
    this.visibleProfile = true;
  }

  editProfile(): void {
    if (this.formProfile.invalid || !this.user()) return;

    this.isSavingProfile = true;
    this._userService
      .updateUser(this.user()!.id, this.formProfile.value)
      .subscribe({
        next: (user) => {
          this.user.set(user);
          this.visibleProfile = false;
          this.isSavingProfile = false;
          this._messageService.add({
            severity: 'success',
            summary: 'Perfil actualizado',
            detail: 'Tus datos se han actualizado correctamente',
          });
        },
        error: () => {
          this.isSavingProfile = false;
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el perfil',
          });
        },
      });
  }

  onSubmitChangePassword(changePasswordForm: any): void {
    if (changePasswordForm.formChangePassword.invalid) {
      changePasswordForm.formChangePassword.markAllAsTouched();
      return;
    }

    this._authService
      .changePassword(
        changePasswordForm.formChangePassword.value.password,
        changePasswordForm.formChangePassword.value.currentPassword,
        changePasswordForm.formChangePassword.value.passwordConfirmation
      )
      .subscribe({
        next: () => {
          this.visibleSecurity = false;
          this._messageService.add({
            severity: 'success',
            summary: 'Contraseña actualizada',
            detail: 'Contraseña actualizada correctamente',
          });
        },
        error: (err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Contraseña no actualizada',
            detail: err?.error?.error?.message || 'Error al cambiar la contraseña',
          });
        },
      });
  }

  logout() {
    this._confirmationService.confirm({
      message: '¿Estás seguro de que deseas cerrar sesión?',
      header: 'Confirmar Cierre de Sesión',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Salir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this._authService.logout();
      }
    });
  }
}
